import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { useRealtimeNotifications } from '@/hooks/use-realtime-notifications';
import { Suspense, ReactNode } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Outlet } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileLayout from "./MobileLayout";
import { SEOManager } from "@/components/seo/SEOManager";

// Custom lightweight loading states
const NavbarSkeleton = () => (
  <div className="h-16 bg-background border-b">
    <div className="container mx-auto px-4 h-full flex items-center justify-between">
      <Skeleton className="h-8 w-32" />
      <div className="flex space-x-4">
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-24" />
        <Skeleton className="h-6 w-24" />
      </div>
      <Skeleton className="h-10 w-10 rounded-full" />
    </div>
  </div>
);

const ContentSkeleton = () => (
  <div className="container mx-auto p-4">
    <div className="space-y-4">
      <Skeleton className="h-8 w-[250px]" />
      <Skeleton className="h-4 w-[300px]" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-32 w-full rounded-md" />
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-lg mt-8" />
    </div>
  </div>
);

interface LayoutProps {
  children?: ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  useRealtimeNotifications();
  const isMobile = useIsMobile();

  // Mobile Layout - no longer lazy loaded
  if (isMobile) {
    return (
      <>
        <SEOManager />
        <MobileLayout>
          <Suspense fallback={<ContentSkeleton />}>
            {children || <Outlet />}
          </Suspense>
        </MobileLayout>
      </>
    );
  }

  // Desktop Layout
  return (
    <>
      <SEOManager />
      <div className="min-h-screen flex flex-col">
        <Suspense fallback={<NavbarSkeleton />}>
          <Navbar />
        </Suspense>
        
        <main className="flex-1">
          <Suspense fallback={<ContentSkeleton />}>
            {children || <Outlet />}
          </Suspense>
        </main>

        <Suspense fallback={<Skeleton className="h-48" />}>
          <Footer />
        </Suspense>
      </div>
    </>
  );
};
